<template>
  <available-section section="reports">
    <available-section section="reports">
      <main class="report-service">
        <header class="report-service__header">
          <h1 class="report-service__title">Услуги: ДМС</h1>
          <form class="report-service__form">
            <div class="report-service__row">
              <date-picker-dialog
                range
                v-bind:value="period"
                v-on:close="onCloseDatePickerDialog"
                v-on:input="onInputPeriod"
              >
                <template v-slot:trigger="{ listenerObject }">
                  <button
                    title="Изменить период"
                    type="button"
                    v-if="hasSelectedFrom"
                    v-on="listenerObject"
                    class="report-service__search"
                  >
                    <time v-bind:datetime="$options.convertToInputDate(periodValue[0])">
                      {{ $options.formatDate(periodValue[0]) }}
                    </time>
                    —
                    <time v-bind:datetime="$options.convertToInputDate(periodValue[1])">
                      {{ $options.formatDate(periodValue[1]) }}
                    </time>
                  </button>
                  <button
                    title="Выбрать период"
                    type="button"
                    v-else
                    v-on="listenerObject"
                    class="report-service__search"
                  >Выберите период</button>
                </template>
              </date-picker-dialog>
            </div>
            <div class="report-service__tags">
              <label
                class="report-service__tag"
                v-for="{ id, label, title } in $options.presetPeriods"
                v-bind:key="'preset-period:' + id"
                v-bind:class="{ active: selectedPresetPeriod === id }"
                v-bind:title="title">
                <input
                  v-bind:checked="id === selectedPresetPeriod"
                  v-bind:id="'report-period-' + id"
                  v-on:input="onInputPresetPeriod(id)"
                  name="report-period"
                  type="radio" />
                <span>{{ label }}</span>
              </label>
            </div>
            <multiple-autocomplete-input
              chips
              class="report-service__autocomplete"
              label="ФИО сотрудника"
              multiple
              removal-per-chip
              select-all-text="Все сотрудники"
              set-select-all-option
              small-chips
              v-bind:items="employees"
              v-bind:value="selectedEmployees"
              v-on:input="onInputEmployees" />
            <multiple-autocomplete-input
              chips
              class="report-service__autocomplete"
              label="ФИО пациента"
              multiple
              removal-per-chip
              select-all-text="Все пациенты"
              set-select-all-option
              small-chips
              v-bind:items="patients"
              v-bind:value="selectedPatients"
              v-on:input="onInputPatients" />
          </form>
        </header>
        <div class="report-service__body">
          <table
            class="report-service__table"
            v-if="hasLines">
            <thead class="report-service__table__header">
              <tr>
                <th
                  v-for="{ field, sorts, title } in columns"
                  v-bind:key="'column:' + field">
                  <sort-by-column
                    class="report-service__table__sort"
                    v-bind:field="field"
                    v-bind:items="sorts"
                    v-bind:label="title"
                    v-bind:value="sort"
                    v-if="sorts.length"
                    v-on:input="onInputSort" />
                  <template v-else>{{ title }}</template>
                </th>
              </tr>
            </thead>
            <tbody>
              <template
                v-for="{
                  fields,
                  key,
                  tags,
                } in bodyLines">
                <tr
                  class="report-service__table__row"
                  v-bind:key="key">
                  <td
                    class="report-service__table__cell"
                    v-for="{ field } in columns"
                    v-bind:data-field="field"
                    v-bind:key="'line:' + key + ':' + field">
                    <label
                      class="report-service__table__expansion"
                      v-if="field === 'title' && tags.includes('parent')"
                    >
                      <input
                        type="checkbox"
                        v-bind:value="key"
                        v-model="expandedLines">
                      <span />
                    </label>
                    {{ fields[field] }}
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
          <i v-else-if="isFetchingReport">Идет построение отчета...</i>
          <strong v-else>Данные по отчету за выбранный период отсутствуют</strong>
        </div>
      </main>
    </available-section>
  </available-section>
</template>

<script>
import DatePickerDialog from '@/components/DatePickerDialog.vue';
import MultipleAutocompleteInput from '@/components/MultipleAutocompleteInput.vue';
import { convertToInputDate, formatDate } from '@/utils/timeFormat';
import AvailableSection from '@/views/ClinicPanel/AvailableSection.vue';

const dayMilliseconds = 24 * 60 * 60 * 1000;

const weekMilliseconds = 24 * 60 * 60 * 1000 * 7;

/**
 * @description Получить период от месяца назад по сегодняшний день.
 * @returns {[Date, Date]}
 */
const getPeriodFromMonthAgoToToday = () => {
  const to = new Date();
  const currentMonth = to.getMonth();
  const from = new Date();
  from.setMonth(currentMonth - 1);
  return [from, to];
};

/**
 * @description Компонент сортировки по столбцу.
 * @type {Vue}
 */
const SortByColumn = {
  computed: {
    headTitle() {
      const head = this.sortedItems[0];
      return head ? head.title : '';
    },
    isNotSelected() {
      return this.items.some(({ value }) => value === this.value) !== true;
    },
    sortedItems() {
      const items = [
        {
          checked: this.isNotSelected,
          title: 'Без сортировки',
          value: '',
        },
        ...this.items.map(({ title, value }) => ({
          checked: value === this.value,
          title,
          value,
        })),
      ];
      let slicedPosition = 0;
      const checkedIndex = items.findIndex(({ checked }) => checked);
      if (checkedIndex !== -1) {
        slicedPosition = checkedIndex + 1;
      }
      if (slicedPosition === items.length) {
        return items;
      }
      return [
        ...items.slice(slicedPosition),
        ...items.slice(0, slicedPosition),
      ];
    },
  },
  render(createElement) {
    const name = `report-sort-${this.field}`;
    return createElement('label', { attrs: { title: this.headTitle } }, [
      createElement('span', this.label).children[0],
      ...this.sortedItems.map(({ checked, title, value }) => createElement('input', {
        attrs: {
          'aria-label': title,
          checked,
          name,
          title,
          type: 'radio',
        },
        domProps: { checked },
        key: `column:${this.field}:sort:${value}`,
        on: { input: () => { this.$emit('input', value); } },
      })),
    ]);
  },
  props: {
    field: {
      required: true,
      type: String,
    },
    items: {
      required: true,
      type: Array,
    },
    label: {
      required: true,
      type: String,
    },
    value: {
      required: true,
      type: String,
    },
  },
};

/**
 * @description Получить период вчерашнего дня.
 * @returns {[Date, Date]}
 */
const getPeriodByYesterday = () => {
  const from = new Date();
  from.setTime(from.getTime() - dayMilliseconds);
  return [from, from];
};

/**
 * @description Получить период от недели назад по сегодняшний день.
 * @returns {[Date, Date]}
 */
const getPeriodFromWeekAgoToToday = () => {
  const to = new Date();
  const from = new Date();
  from.setTime(from.getTime() - weekMilliseconds);
  return [from, to];
};

/**
 * @description Получить период от года назад по сегодняшний день.
 * @returns {[Date, Date]}
 */
const getPeriodFromYearAgoToToday = () => {
  const to = new Date();
  const currentYear = to.getFullYear();
  const from = new Date();
  from.setFullYear(currentYear - 1);
  return [from, to];
};

/**
 * @description Получить периода по предварительно определенному периоду.
 * @param {string} preset Идентификатор периода.
 * @returns {([Date, Date]|[])}
 */
const getPeriodByPreset = (preset) => {
  if (preset === 'week') {
    return getPeriodFromWeekAgoToToday();
  }
  if (preset === 'month') {
    return getPeriodFromMonthAgoToToday();
  }
  if (preset === 'year') {
    return getPeriodFromYearAgoToToday();
  }
  if (preset === 'yesterday') {
    return getPeriodByYesterday();
  }
  return [];
};

/**
 * @description Предварительные периоды.
 */
const presetPeriods = [
  {
    id: 'yesterday',
    label: 'Вчера',
    title: 'Показать отчет за вчерашний день',
  },
  {
    id: 'week',
    label: 'Неделя',
    title: 'Показать отчет от прошлой недели',
  },
  {
    id: 'month',
    label: 'Месяц',
    title: 'Показать отчет от прошлого месяца',
  },
  {
    id: 'year',
    label: 'Год',
    title: 'Показать отчет от прошлого года',
  },
];

/**
 * @description Раздел отчета пациентов по администраторам.
 * @type {Vue}
 */
export default {
  components: {
    AvailableSection,
    DatePickerDialog,
    MultipleAutocompleteInput,
    SortByColumn,
  },
  computed: {
    bodyLines() {
      const render = (path) => ({ fields, services, tags }, index) => {
        const depth = path.length;
        const key = [...path, index].join(':');
        const line = {
          depth,
          fields,
          key,
          tags,
        };
        if (tags.includes('parent') && this.expandedLines.includes(key)) {
          return [line, ...services.flatMap(render([...path, index]))];
        }
        return [line];
      };
      return this.lines.flatMap(render(['line']));
    },
    /**
     * @description Есть строки отчета.
     * @type {boolean}
     */
    hasLines() {
      return this.lines.length > 0;
    },
    /**
     * @description Имеет выбранную дату окончания.
     * @type {boolean}
     */
    hasSelectedTo() {
      return this.period.length === 2;
    },
    /**
     * @description Имеет выбранную дату начала.
     * @type {boolean}
     */
    hasSelectedFrom() {
      return this.period.length > 0;
    },
    /**
     * @description Период.
     * @type {Date[]}
     */
    periodValue() {
      if (this.period.length === 1) {
        const [from] = this.period;
        return [from, from];
      }
      if (this.period.length === 2) {
        return this.period;
      }
      return [];
    },
  },
  convertToInputDate,
  created() {
    this.fetchLines();
    this.fetchFilters();
  },
  data() {
    const selectedPresetPeriod = 'month';
    return {
      columns: [],
      expandedLines: [],
      employees: [],
      patients: [],
      isFetchingReport: false,
      lines: [],
      period: getPeriodByPreset(selectedPresetPeriod),
      selectedEmployees: [],
      selectedPatients: [],
      selectedPresetPeriod,
      sort: '',
    };
  },
  formatDate,
  methods: {
    /**
     * @description Получить колонки и строки отчета.
     * @returns {void}
     */
    fetchLines() {
      if (this.hasSelectedFrom !== true) {
        return;
      }
      this.isFetchingReport = true;
      this
        .$reportGateway
        .getServicesByDms(
          this.selectedEmployees,
          this.selectedPatients,
          convertToInputDate(this.period[0]),
          convertToInputDate(this.period[1]),
          this.sort,
        )
        .then((result) => {
          result.forEach(({ lines, columns }) => {
            this.expandedLines = [];
            this.lines = lines;
            this.columns = columns;
          });
        })
        .finally(() => { this.isFetchingReport = false; });
    },
    /**
     * @description Получить фильтры.
     * @returns {void}
     */
    fetchFilters() {
      this
        .$reportGateway
        .getServicesFiltersByDms()
        .then((result) => {
          result.forEach(({ employees, patients }) => {
            this.employees = employees;
            this.patients = patients;
          });
        });
    },
    /**
     * @description Обработать закрытие диалога выборы даты.
     * @returns {void}
     */
    onCloseDatePickerDialog() {
      if (this.period.length === 1) {
        const [left] = this.period;
        this.onInputPeriod([left, left]);
      }
    },
    onInputEmployees(value) {
      this.selectedEmployees = value;
      this.fetchLines();
    },
    onInputPatients(value) {
      this.selectedPatients = value;
      this.fetchLines();
    },
    /**
     * @description Обработать выбор периода.
     * @param {[Date, Date]} value Значение.
     * @returns {void}
     */
    onInputPeriod(value) {
      this.selectedPresetPeriod = '';
      this.period = value.sort(
        (first, second) => Number(first.getTime() > second.getTime()),
      );
      if (this.period.length === 2) {
        this.fetchLines();
      }
    },
    /**
     * @description Обработать выбор предварительно заданного периода.
     * @param {string} value Идентификатор выбранного периода.
     * @returns {void}
     */
    onInputPresetPeriod(value) {
      this.selectedPresetPeriod = value;
      const periodByPreset = getPeriodByPreset(value);
      if (periodByPreset.length === 0) {
        return;
      }
      this.period = periodByPreset;
      this.fetchLines();
    },
    onInputSort(value) {
      this.sort = value;
      this.fetchLines();
    },
  },
  name: 'ClinicPanelReportServicesDms',
  presetPeriods,
};
</script>

<style lang="scss">
.menuable__content__active {
  min-width: 250px !important;
}
.report-service {
  width: 100%;
  height: 100%;
  overflow-y: auto;
  &__header {
    background: #F2F5F8;
    width: 100%;
    padding: 38px 20px 48px 23px;
  }
  &__form {
    display: flex;
    flex-flow: row wrap;
    align-items: flex-end;
    gap: 23px;
    margin: 30px 0 0 0;
  }
  &__row {
    display: flex;
    flex-direction: row;
  }
  &__search {
    border: 1px solid #C2CFE0;
    border-radius: 4px;
    background: #FFFFFF;
    font-weight: 400;
    font-size: 16px;
    line-height: 19px;
    height: 40px;
    min-width: 250px;
    width: 100%;
    padding: 0 0 0 10px;
    display: flex;
    align-items: center;
    position: relative;
    &:after {
      content: '';
      background-color: #EAEFF3;
      border-radius: 0 4px 4px 0;
      width: 38px;
      height: 38px;
      position: absolute;
      right: 0;
      overflow: hidden;
      background-image: url("@/assets/img/calendar-grey.svg");
      background-position: center;
    }
  }
  &__tags {
    display: flex;
    flex-direction: row;
    align-items: center;
    border-radius: 4px;
    overflow: hidden;
  }
  &__tag {
    &>input {
      position: absolute;
      z-index: -1;
      opacity: 0;
      &:checked+span {
        background: #0489E0;
        color: #FFFFFF;
      }
    }
    &>span {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 40px;
      background: #EAEFF3;
      font-weight: 400;
      font-size: 14px;
      line-height: 17px;
      color: #192A3E;
      padding: 0 13px;
      cursor: pointer;
      &:hover {
        background: #0489E0;
        color: #FFFFFF;
      }
    }
  }
  &__autocomplete {
    min-width: 300px !important;
    max-width: fit-content;
    &.v-input .v-label {
      height: 32px;
      line-height: 32px;
    }
    &.v-text-field .v-label {
      top: 7px;
    }
    label.v-label.v-label--active.theme--light {
      top: 0;
    }
    &.v-text-field .v-input__append-inner, .v-text-field .v-input__prepend-inner {
      margin-top: 12px;
    }
  }
  &__checkbox {
    display: flex;
    align-items: center;
    flex-direction: row;
    gap: 10px;
    font-weight: 400;
    font-size: 18px;
  }
  &__percent {
    position: relative;
    width: 38px;
    height: 26px;
    input {
      position: absolute;
      opacity: 0;
      width: 38px;
      height: 26px;
      &:checked + .slider {
        background-color: rgba(4, 137, 224, 0.1);
        border: 1px solid #0489E0;
      }
      &:checked + .slider::before {
        background-color: #0489E0;
        -webkit-transform: translateX(12px);
        -ms-transform: translateX(12px);
        transform: translateX(12px);
      }
    }
    .slider {
      position: absolute;
      cursor: pointer;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      border-radius: 34px;
      border: 1px solid #c2cfe0;
      transition: .2s;
      &::before {
        position: absolute;
        top: 2px;
        left: 2px;
        content: "";
        height: 20px;
        width: 20px;
        border-radius: 50%;
        background-color: #90a0b7;
        transition: .2s;
      }
    }
    .text {
      position: absolute;
      left: 50px;
      cursor: pointer;
      font-weight: 400;
      font-size: 18px;
    }
  }
  &__body {
    padding: 37px 20px 48px 23px;
    overflow: auto;
  }
  &__table {
    font-size: 14px;
    line-height: 14px;
    border-collapse: collapse;
    width: 100%;
    overflow: auto;
    colgroup {
      col:nth-child(2n) {
        background: #F4F7F9;
      }
    }
    &__header {
      font-weight: 700;
      border-bottom: 2px solid #0489E0;
      th {
        padding: 10px 2px;
        label {
          input {
            display: none;
          }
        }
      }
      th {
        text-align: left;
        &:first-child {
          padding: 10px 8px;
        }
      }
    }
    &__expansion {
      position: relative;
      &>input {
        position: absolute;
        z-index: -1;
        opacity: 0;
        &:checked+span {
          transform: rotate(180deg);
          background-position: top;
        }
      }
      &>span {
        display: inline-flex;
        align-items: center;
        user-select: none;
        cursor: pointer;
        background-image: url("@/assets/img/checked/arrow-top-blue.svg");
        background-size: contain;
        background-position: bottom;
        width: 15px;
        height: 15px;
        margin: 0 17px 0 0;
      }
    }
    &__row {
      border-bottom: 1px solid #EAEFF3;
      td {
        &:first-child {
          text-align: left;
          padding: 15px 8px;
        }
      }
    }
    &__cell {
      padding: 15px 2px;
      text-align: left;
    }
    &__sort {
      cursor: pointer;
      &:after {
        background-image: url("@/assets/img/checked/checked-grey.svg");
        background-position: center;
        background-repeat: no-repeat;
        background-size: contain;
        content: '';
        display: inline-block;
        height: 8px;
        margin: 0 0 0 2px;
        min-height: 8px;
        min-width: 8px;
        width: 8px;
      }
    }
  }
}
@media screen and (max-width: 1500px) {
  .report-service {
    &__table {
      font-size: 13px;
    }
  }
}
@media screen and (max-width: 1240px) {
  .report-service {
    &__table {
      width: 1200px;
    }
  }
}
@media screen and (max-width: 480px) {
  .menuable__content__active {
    min-width: initial;
  }
  .report-service {
    &__header {
      padding: 40px 15px;
    }
    &__body {
      padding: 22px 15px 50px;
    }
    &__autocomplete {
      min-width: initial;
    }
  }
}
</style>
